import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Award, Users, CheckCircle2, MessageSquare, Megaphone, Check } from "lucide-react";
import { myNotifications, markNotificationRead, markAllNotificationsRead } from "@/lib/apiClient";
import { useI18n } from "@/contexts/I18nContext";

const TYPE_ICON = {
  peer_review_received: CheckCircle2,
  peer_review_pending: Users,
  achievement_earned: Award,
  feedback_moderated: MessageSquare,
  admin_broadcast: Megaphone,
};

function timeAgo(iso, t) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return t("notif.just_now");
  if (mins < 60) return t("notif.minutes_ago", { n: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t("notif.hours_ago", { n: hrs });
  const days = Math.floor(hrs / 24);
  if (days < 7) return t("notif.days_ago", { n: days });
  return new Date(iso).toLocaleDateString();
}

/**
 * Notification bell — sits beside the user-menu avatar in the header.
 * Polls every 60s so the unread badge stays current without the user
 * having to reload; the dropdown itself refetches on open for freshness.
 */
export default function NotificationBell() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const loadedOnce = useRef(false);

  const load = async () => {
    try {
      const r = await myNotifications(30);
      setItems(r?.items || []);
      setUnreadCount(r?.unread_count || 0);
    } catch { /* non-blocking */ }
  };

  useEffect(() => {
    load();
    loadedOnce.current = true;
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  const onOpen = () => {
    setOpen((v) => !v);
    if (!open) load();
  };

  const onItemClick = async (n) => {
    if (!n.read) {
      setItems((prev) => prev.map((i) => (i.notification_id === n.notification_id ? { ...i, read: true } : i)));
      setUnreadCount((c) => Math.max(0, c - 1));
      try { await markNotificationRead(n.notification_id); } catch { /* non-blocking */ }
    }
    setOpen(false);
  };

  const onMarkAllRead = async () => {
    setItems((prev) => prev.map((i) => ({ ...i, read: true })));
    setUnreadCount(0);
    try { await markAllNotificationsRead(); } catch { /* non-blocking */ }
  };

  return (
    <div className="relative" onMouseEnter={() => { if (loadedOnce.current) load(); }}>
      <button
        data-testid="notification-bell"
        onClick={onOpen}
        title={t("notif.notifications")}
        className="relative w-9 h-9 rounded-full bg-[#FDFBF7] border border-[#E7E0D2] grid place-items-center hover:border-[#064E3B] transition">
        <Bell className="w-4 h-4 text-[#064E3B]" />
        {unreadCount > 0 && (
          <span data-testid="notification-unread-badge"
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#EF4444] text-white text-[9px] font-bold grid place-items-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          {/* end-0, not right-0 — the header toolbar this bell sits in
              mirrors under dir="rtl", so the bell isn't reliably near the
              physical right edge any more once it does. end-0
              (inset-inline-end) tracks whichever side it actually lands on
              instead of running the panel off the opposite edge. */}
          <div data-testid="notification-panel"
            className="absolute end-0 top-full pt-2 w-80 z-50">
            <div className="rounded-2xl bg-white border border-[#E7E0D2] shadow-[0_20px_50px_-15px_rgba(6,78,59,0.25)] overflow-hidden">
              <div className="px-5 py-3 border-b border-[#E7E0D2] flex items-center justify-between">
                <span className="font-semibold text-sm text-[#064E3B]">{t("notif.notifications")}</span>
                {unreadCount > 0 && (
                  <button data-testid="notification-mark-all-read" onClick={onMarkAllRead}
                    className="inline-flex items-center gap-1 text-xs text-[#064E3B] hover:underline">
                    <Check className="w-3 h-3" /> {t("notif.mark_all_read")}
                  </button>
                )}
              </div>
              <div className="max-h-96 overflow-y-auto">
                {items.length === 0 ? (
                  <div className="px-5 py-8 text-center text-sm text-[#4B5563]">{t("notif.empty")}</div>
                ) : (
                  items.map((n) => {
                    const Icon = TYPE_ICON[n.type] || Bell;
                    const row = (
                      <div className={`flex items-start gap-3 px-5 py-3 transition ${n.read ? "bg-white" : "bg-[#ECFDF5]"} hover:bg-[#F9F5EE]`}>
                        <div className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${n.read ? "bg-[#F5EFE3] text-[#4B5563]" : "bg-[#064E3B] text-white"}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm ${n.read ? "text-[#1F2937]" : "font-semibold text-[#064E3B]"}`}>{n.title}</div>
                          {n.body && <div className="text-xs text-[#4B5563] mt-0.5 line-clamp-2">{n.body}</div>}
                          <div className="text-[10px] text-[#9CA3AF] mt-1">{timeAgo(n.created_at, t)}</div>
                        </div>
                        {!n.read && <span className="w-2 h-2 rounded-full bg-[#D97706] mt-1.5 shrink-0" />}
                      </div>
                    );
                    return n.link ? (
                      <Link key={n.notification_id} to={n.link} data-testid={`notification-${n.notification_id}`} onClick={() => onItemClick(n)}>
                        {row}
                      </Link>
                    ) : (
                      <button key={n.notification_id} data-testid={`notification-${n.notification_id}`} onClick={() => onItemClick(n)} className="w-full text-left">
                        {row}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
